import React from 'react';

import { GestureEvent } from '../../../../../Events/withEvent';
import { useIsMobile } from '../../../../../Web/Context/PlatformContext/PlatformContext';
import { useGestureContext, useMultiTouch } from '../../../Context/GestureContext';
import { useMapProvider } from '../../../Context/MapContext';
import { useUndoRedoMap } from '../../../Context/UndoRedoMapContext';
import { RoomItemFactory } from '../../../Helpers/BaseRoomItemFactory';
import { MapConstants, MapMode } from '../../../MapConstants';
import { IRoomItem } from '../../../Models/IRoomItem';
import { useMenuContext } from '../MenuContext/MenuContext';
import { useMenuDeleteDialog } from '../MenuContext/MenuDeleteDialog';
import { ObjectUtils } from '../../../../../Helpers/ObjectUtils';
import { MapHelper } from 'Plugins/FloorPlan/Helpers/MapHelper';
import { PointUtils } from 'Plugins/FloorPlan/Utils/PointUtils';
import { useMapViewerContext } from 'Plugins/FloorPlan/Context/MapViewerContext';
import { EventUtils } from 'Plugins/FloorPlan/Utils/EventUtils';

export const useRoomItemGestureController = (item: IRoomItem) => {
    const map = useMapProvider();
    const playground = useMapViewerContext();
    const isMobile = useIsMobile();
    const multiTouch = useMultiTouch();
    const menuContext = useMenuContext();
    const undoRedo = useUndoRedoMap();
    const gestureContext = useGestureContext();

    const onPressDown = React.useCallback(
        (event: GestureEvent) => {
            if (!multiTouch.handleMultiTouch(event as React.TouchEvent)) {
                if (!menuContext.showMenuContext && !MapConstants.mode) {
                    undoRedo.store(map.getState());

                    const snap = EventUtils.calculSnapFromSyntheticEvent(
                        event,
                        MapConstants.grid_snap,
                        playground.svgViewer!.getValue(),
                        (playground.svgViewer as any)?.ViewerDOM
                    );
                    gestureContext.update({
                        isPressDown: true,
                        initialRoomItem: { id: item.roomItemId!, coordsReal: item.coordsReal! },
                        initialSnap: snap,
                    });

                    const { rooms = [] } = map.getState();
                    const roomItems = MapHelper.getRoomItems(rooms);
                    const roomItemMoveOrPlacingInit = ObjectUtils.clone(
                        roomItems.find((x) => x.roomItemId === item.roomItemId)
                    );
                    map.update({
                        values: { roomItemMoveOrPlacingInit, panZoomDisabled: true, busy: true },
                        logs: { event: 'useRoomItemGestureController onPressDown' },
                    });
                    MapConstants.mode = MapMode.MODE_TYPE_MOVE_ROOM_ITEM;
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [menuContext.showMenuContext, item.coordsReal]
    );

    const onMouseUp = React.useCallback(
        (event: GestureEvent) => {
            if (!isMobile && gestureContext.getState().isPressDown && !menuContext.showMenuContext) {
                menuContext.show();
                const { selectedRoomItemId } = map.getState();
                if (selectedRoomItemId !== item.roomItemId) {
                    map.setPanel({
                        values: { selectedRoomItemId: item.roomItemId, panZoomDisabled: false, busy: false },
                    });
                }
                gestureContext.clear();
                MapConstants.mode = undefined;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [menuContext.showMenuContext, isMobile]
    );

    const onTouchEnd = React.useCallback(
        (event: GestureEvent) => {
            event.preventDefault(); //To prevent mouse events being fired
            if (menuContext.showMenuContext) {
                menuContext.close();
                event.stopPropagation(); //To prevent TouchEndController being fired
            }
            if (gestureContext.getState().isPressDown && !menuContext.showMenuContext) {
                menuContext.show();
                map.setPanel({ values: { panZoomDisabled: false, busy: false } });
                gestureContext.clear();
                MapConstants.mode = undefined;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [menuContext.showMenuContext]
    );

    //* ACTIONS
    const onMove = React.useCallback((event?: GestureEvent) => {
        if (!MapConstants.mode) {
            menuContext.close();
            const { rooms = [] } = map.getState();
            const roomItems = MapHelper.getRoomItems(rooms);
            for (const r of roomItems) {
                if (r.roomItemId === item.roomItemId) r.isMoving = true;
                return r;
            }
            map.setPanel({ values: { rooms, panZoomDisabled: true, busy: true } });
            gestureContext.update({ initialRoomItem: { id: item.roomItemId!, coordsReal: item.coordsReal! } });
            MapConstants.mode = MapMode.MODE_TYPE_MOVE_ROOM_ITEM;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteDialog = useMenuDeleteDialog({
        description: `Supprimer l'aménagement "${item.name}"`,
        confirmDelete: () => map.deleteRoomItem(item.roomItemId!),
    });

    const onModify = React.useCallback((event?: GestureEvent) => {
        if (!MapConstants.mode) {
            menuContext.close();
            map.setPanel({ values: { selectedRoomItemId: item.roomItemId } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDuplicate = React.useCallback((event?: GestureEvent) => {
        if (!MapConstants.mode) {
            menuContext.close();
            const { rooms = [] } = map.getState();
            const roomItems = MapHelper.getRoomItems(rooms);
            const room = rooms.find((r) => r.roomId === item.roomId);
            const baseRoomItem = roomItems.find((r) => r.roomItemId === item.roomItemId)!;

            const name = RoomItemFactory.getNewName(baseRoomItem, roomItems);
            const newRoomItem = RoomItemFactory.createRoomItem({ ...baseRoomItem, name });
            room?.roomItems.push(newRoomItem);
            map.setPanel({
                values: { rooms, panZoomDisabled: true, busy: true },
                mapModified: true,
            });
            gestureContext.update({
                initialRoomItem: { id: newRoomItem.roomItemId!, coordsReal: newRoomItem.coordsReal! },
                initialSnap: PointUtils.bboxCenter(newRoomItem.coordsReal!),
            });
            MapConstants.mode = MapMode.MODE_TYPE_ROOM_ITEM_PLACING;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDelete = React.useCallback((event?: GestureEvent) => {
        if (!MapConstants.mode) {
            menuContext.close();
            deleteDialog.requestDelete();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        onMove,
        onModify,
        onDelete,
        onDuplicate,
        menuContext,
        gestures: {
            onMouseDown: onPressDown,
            onMouseUp,
            onTouchStart: onPressDown,
            onTouchEnd,
        },
    };
};
